import type {
  CogniteClient,
  DatapointAggregates,
  Datapoints,
  DatapointsQuery,
  DatapointsQueryProperties,
} from '@cognite/sdk';
import type { QueryClient } from '@tanstack/react-query';

import { chunk } from '../utils/chunk.ts';
import { CDF_DATAPOINTS_BATCH_SIZE } from './cdf-constants.ts';

/**
 * Single-item shape accepted by `client.datapoints.retrieve` — `id`,
 * `externalId`, or `instanceId` discriminates the lookup form.
 */
export type DatapointsBatchItem = DatapointsQuery;

/**
 * Result row returned by `client.datapoints.retrieve` — either raw points
 * (no `aggregates`) or aggregate buckets. Both share the metadata shape
 * we key off (`externalId`, `instanceId`).
 */
export type DatapointsBatchResult = Datapoints | DatapointAggregates;

interface FetchDatapointsBatchedOptions {
  client: CogniteClient;
  queryClient: QueryClient;
  items: readonly DatapointsBatchItem[];
  spec?: DatapointsQueryProperties;
  /** TanStack Query stale time in ms. Defaults to 60s. */
  staleTime?: number;
}

const DEFAULT_STALE_TIME_MS = 60_000;

/**
 * Canonical identity string for a single fetch item. Used to:
 *  - sort items into a stable order (so identical input sets hash to the
 *    same query key regardless of caller-side iteration order),
 *  - serve as the lookup key when assembling results into a `Map`.
 *
 * Lookup form is preserved in the prefix so an `externalId: "x"` request
 * and an `instanceId: { space: "s", externalId: "x" }` request — which
 * may resolve to the same series server-side but are distinct API calls
 * — get distinct cache entries. The SDK can return different responses
 * for the two forms (e.g. when a series has no `externalId` set).
 */
function getItemKey(item: DatapointsBatchItem): string {
  if ('instanceId' in item && item.instanceId) {
    return `instance:${item.instanceId.space}/${item.instanceId.externalId}`;
  }
  if ('externalId' in item && item.externalId !== undefined) {
    return `ext:${item.externalId}`;
  }
  if ('id' in item && item.id !== undefined) {
    return `id:${item.id}`;
  }
  return 'unknown';
}

/**
 * Composite identity for a datapoints fetch result / request item.
 * Pairs the (optional) instance space with the externalId so two series
 * with the same externalId across different spaces don't collide. Shared
 * between {@link getResultKey} (result side) and the request side (the
 * timeseries provider's datapoints fetch) so producer and consumer key
 * the same way.
 */
export function datapointsResultKey(ref: { space?: string; externalId: string }): string {
  return [ref.space, ref.externalId].filter(Boolean).join('|');
}
/**
 * Build the CDF `retrieve` item for a single datapoints reference — the
 * `instanceId` query form when an instance `space` is known, bare
 * `externalId` otherwise. The request-form counterpart to
 * {@link datapointsResultKey}'s space-presence collapse; colocated so the
 * request item and its result key share the same `(space, externalId)`
 * discrimination and cannot drift.
 */
export function toRetrieveItem(ref: { space?: string; externalId: string }): DatapointsBatchItem {
  return ref.space !== undefined && ref.space.length > 0
    ? { instanceId: { space: ref.space, externalId: ref.externalId } }
    : { externalId: ref.externalId };
}

/**
 * Composite identity used to key the result map. Pairs the timeseries
 * space (if known) with its externalId so two series sharing an
 * externalId across different spaces do not collide in the map. The
 * space prefix comes from `instanceId.space` when CDF resolved the
 * series via an instance-id query; for plain externalId queries the
 * space slot is empty (`|<externalId>`). Mirrors the `${space ?? ''}|${externalId}`
 * convention used by the timeseries provider's datapoints fetch to look
 * results up by the original {@link DatapointsBatchItem}.
 */
function getResultKey(result: DatapointsBatchResult): string | undefined {
  const withInstance = result as { instanceId?: { space?: string; externalId?: string } };
  const externalId = result.externalId ?? withInstance.instanceId?.externalId;
  if (externalId === undefined) {
    return undefined;
  }
  return datapointsResultKey({ space: withInstance.instanceId?.space, externalId });
}
/**
 * Assemble a flat sequence of datapoints results into a `Map` keyed by
 * externalId (with `instanceId.externalId` fallback via {@link getResultKey}).
 *
 * Shared between {@link fetchDatapointsBatched} (async producer-context
 * path) and `useTimeSeriesDatapoints` (React-render path) so both produce
 * identically-shaped output. Series whose key cannot be resolved are
 * dropped — `ignoreUnknownIds: true` on the SDK side already filters
 * unknown series, so this only affects the malformed-response edge case.
 */
export function assembleDatapointsMap(series: Iterable<DatapointsBatchResult>): Map<string, DatapointsBatchResult> {
  const map = new Map<string, DatapointsBatchResult>();
  for (const s of series) {
    const key = getResultKey(s);
    if (key !== undefined) {
      map.set(key, s);
    }
  }
  return map;
}
/**
 * Single SDK call wrapper used by both the async kernel and the React
 * hook's `queryFn`. Centralises the `items` / `spec` / `ignoreUnknownIds`
 * call shape and the response narrowing so neither call site repeats
 * the SDK boilerplate.
 *
 * Accepts `client: CogniteClient | undefined` so the React hook — whose
 * `useCogniteClient` may transiently return `undefined` during init —
 * can invoke this without a separate null-check; in that case the call
 * short-circuits to an empty result list (preserves the existing
 * `client?.datapoints.retrieve(...)` behaviour from before this refactor).
 */
export async function retrieveDatapointsBatch(
  client: CogniteClient | undefined,
  batch: DatapointsBatchItem[],
  spec: DatapointsQueryProperties | undefined,
): Promise<DatapointsBatchResult[]> {
  const result = await client?.datapoints.retrieve({
    items: batch,
    includeOutsidePoints: true,
    ignoreUnknownIds: true,
    ...spec,
  });
  return (result ?? []) as DatapointsBatchResult[];
}

/**
 * Canonical query-key factory for batched datapoints fetches.
 *
 * The key depends only on the canonical identity of each item (via
 * `getItemKey`, sorted) and the spec, so two callers asking for the same
 * series + window hit the same TanStack Query cache entry regardless of
 * request order or surrounding caller. Exported so call sites that build
 * their own `useSuspenseQueries` / `fetchQuery` shells (notably the
 * `useTimeSeriesDatapoints` hook) can produce the same key the async
 * function uses, enabling cross-context cache reuse.
 */
export function datapointsBatchedQueryKey(
  items: readonly DatapointsBatchItem[],
  spec: DatapointsQueryProperties | undefined,
): readonly unknown[] {
  return [
    'datapointsBatched',
    {
      items: items.map(getItemKey).sort(),
      spec: spec ?? {},
    },
  ];
}

/**
 * Batched datapoints fetch with TanStack Query cache dedup.
 *
 * Used by:
 *  - `useTimeSeriesDatapoints` (React render context, via a render-side
 *    `useSuspenseQueries` shell that reuses {@link datapointsBatchedQueryKey})
 *  - `fetchTimeseriesStep` (chat: wide chart-format dataset)
 *  - `enrichTimeseriesRefs` (chat: per-row ref enrichment)
 *
 * Chunks `items` by {@link CDF_DATAPOINTS_BATCH_SIZE}, fetches each chunk
 * in parallel through the provided `queryClient`, and assembles results
 * into a `Map` keyed by externalId. Series absent from the map are either
 * unknown to CDF (filtered server-side via `ignoreUnknownIds: true`) or
 * returned no datapoints in the requested window.
 *
 * Items are sorted by {@link getItemKey} before chunking so identical
 * input sets — across callers and across iterations — produce identical
 * cache keys.
 */
export async function fetchDatapointsBatched({
  client,
  queryClient,
  items,
  spec,
  staleTime = DEFAULT_STALE_TIME_MS,
}: FetchDatapointsBatchedOptions): Promise<Map<string, DatapointsBatchResult>> {
  if (items.length === 0) {
    return new Map();
  }

  const sortedItems = [...items].sort((a, b) => getItemKey(a).localeCompare(getItemKey(b)));
  const batches = chunk(sortedItems, CDF_DATAPOINTS_BATCH_SIZE);

  const batchResults = await Promise.all(
    batches.map((batch) =>
      queryClient.fetchQuery({
        queryKey: datapointsBatchedQueryKey(batch, spec),
        queryFn: () => retrieveDatapointsBatch(client, batch, spec),
        staleTime,
      }),
    ),
  );

  return assembleDatapointsMap(batchResults.flat());
}
